import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

// Type guard for Firestore timestamp
interface FirestoreTimestamp {
   _seconds: number;
   _nanoseconds: number;
}

const isFirestoreTimestamp = (value: any): value is FirestoreTimestamp => {
   return value && typeof value === 'object' && typeof value._seconds === 'number';
};

// Helper function to parse dates from various formats
const parseDate = (dateValue: string | Date | FirestoreTimestamp): Date | null => {
   if (!dateValue) return null;
   
   if (isFirestoreTimestamp(dateValue)) {
      return new Date(dateValue._seconds * 1000);
   } else if (typeof dateValue === 'string') {
      return new Date(dateValue);
   } else if (dateValue instanceof Date) {
      return dateValue;
   }
   
   return null;
};

// Helper function to determine if a subscription provides access
const hasSubscriptionAccess = (subscription: Subscription | null): boolean => {
   if (!subscription) return false;

   // Active subscriptions always have access
   if (subscription.status === 'active') {
      return true;
   }

   // Trialing subscriptions have access (free trial period)
   if (subscription.status === 'trialing') {
      return true;
   }

   // Past due subscriptions still have access (grace period)
   if (subscription.status === 'past_due') {
      return true;
   }

   // For canceled subscriptions, calculate proper end date from start date + interval
   if (subscription.status === 'canceled') {
      const now = new Date();

      // Parse provided end date
      const providedEndDate = parseDate(subscription.ended_at);

      // Calculate end date from start date + interval (more reliable)
      let calculatedEndDate = null;
      const startDate = parseDate(subscription.started_at);

      if (startDate) {
         calculatedEndDate = new Date(startDate);
         // Add the subscription period based on interval
         if (subscription.interval === 'year') {
            calculatedEndDate.setFullYear(calculatedEndDate.getFullYear() + 1);
         } else if (subscription.interval === 'month') {
            calculatedEndDate.setMonth(calculatedEndDate.getMonth() + 1);
         }
      }

      // Use the later of the two dates (provided vs calculated)
      let finalEndDate = null;
      if (providedEndDate && calculatedEndDate) {
         finalEndDate = providedEndDate > calculatedEndDate ? providedEndDate : calculatedEndDate;
      } else if (calculatedEndDate) {
         finalEndDate = calculatedEndDate;
      } else if (providedEndDate) {
         finalEndDate = providedEndDate;
      }

      return finalEndDate ? finalEndDate > now : false;
   }

   // All other statuses (incomplete, unpaid, etc.) don't have access
   return false;
};

export interface Subscription {
   active: boolean;
   status: string;
   plan: string;
   interval: string;
   subscriptionId: string;
   userId: string;
   ended_at: string | Date | FirestoreTimestamp;
   started_at: string | Date | FirestoreTimestamp;
   customerId: string;
}

interface SubscriptionState {
   currentSubscription: Subscription | null;
   allSubscriptions: Subscription[];
   hasActiveSubscription: boolean;
   loading: boolean;
   error: string | null;
}

const initialState: SubscriptionState = {
   currentSubscription: null,
   allSubscriptions: [],
   hasActiveSubscription: false,
   loading: false,
   error: null,
};

// Async thunk to fetch user subscriptions by email
export const fetchUserSubscriptionsByEmail = createAsyncThunk(
   'subscription/fetchByEmail',
   async (email: string, { rejectWithValue }) => {
      try {
         console.log('Redux: Fetching subscription for email:', email);

         const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4001';
         const fullUrl = `${apiUrl}/api/subscriptions/status?email=${encodeURIComponent(email)}`;

         const response = await axios.get(fullUrl);

         console.log('🔥 FRONTEND: Subscription API response:', response.data);

         if (response.data.success) {
            return response.data;
         } else {
            return rejectWithValue(response.data.error || 'Failed to fetch subscription status');
         }
      } catch (error: any) {
         console.error('Redux: Error fetching subscription:', error);
         return rejectWithValue(
            error.response?.data?.error || error.message || 'An unknown error occurred'
         );
      }
   }
);

const subscriptionSlice = createSlice({
   name: 'subscription',
   initialState,
   reducers: {
      // Clear subscription data
      clearSubscription: (state) => {
         state.currentSubscription = null;
         state.allSubscriptions = [];
         state.hasActiveSubscription = false;
         state.error = null;
      },

      // Set subscription data directly (for manual updates)
      setSubscription: (state, action: PayloadAction<Subscription>) => {
         state.currentSubscription = action.payload;
         state.allSubscriptions = [action.payload];
         state.hasActiveSubscription = hasSubscriptionAccess(action.payload);
      },

      // Clear error
      clearError: (state) => {
         state.error = null;
      },
   },
   extraReducers: (builder) => {
      builder
         // Fetch subscriptions by email
         .addCase(fetchUserSubscriptionsByEmail.pending, (state) => {
            state.loading = true;
            state.error = null;
         })
         .addCase(fetchUserSubscriptionsByEmail.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;

            console.log('🔥 Redux: Processing subscription response:', {
               'Backend hasActiveSubscription': action.payload.hasActiveSubscription,
               'Subscription status': action.payload.subscription?.status,
               'Subscription ended_at': action.payload.subscription?.ended_at,
               'Full payload': action.payload,
            });

            if (action.payload.subscription) {
               state.currentSubscription = action.payload.subscription;
               state.allSubscriptions = [action.payload.subscription];
            } else {
               state.currentSubscription = null;
               state.allSubscriptions = [];
            }

            state.hasActiveSubscription = action.payload.hasActiveSubscription || false;

            console.log('🎯 Redux: FINAL subscription state update:', {
               'Final hasActiveSubscription': state.hasActiveSubscription,
               'Final subscription status': state.currentSubscription?.status,
               'Final subscription ended_at': state.currentSubscription?.ended_at,
               'Backend said hasActiveSubscription': action.payload.hasActiveSubscription,
            });
         })
         .addCase(fetchUserSubscriptionsByEmail.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
            state.currentSubscription = null;
            state.allSubscriptions = [];
            state.hasActiveSubscription = false;
         });
   },
});

export const { clearSubscription, setSubscription, clearError } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
